import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to track unsaved changes in a component
 * 
 * @param initialData - The initial data to compare against
 * @param getCurrentData - Function to get the current data state
 * @param isEnabled - Whether to track changes (default: true)
 * @returns Object containing hasChanges flag and utility functions
 */
export function useUnsavedChanges<T>(
  initialData: T,
  getCurrentData: () => T,
  isEnabled: boolean = true
) {
  const [hasChanges, setHasChanges] = useState(false);
  const initialDataRef = useRef<string>(JSON.stringify(initialData));
  const isEnabledRef = useRef(isEnabled);

  // Update enabled state
  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  // Update initial data reference when it changes
  useEffect(() => {
    initialDataRef.current = JSON.stringify(initialData);
  }, [initialData]);

  /**
   * Check if there are unsaved changes
   */
  const checkForChanges = useCallback(() => {
    if (!isEnabledRef.current) {
      setHasChanges(false);
      return false;
    }

    try {
      const currentData = getCurrentData();
      const currentDataString = JSON.stringify(currentData);
      const hasDataChanged = currentDataString !== initialDataRef.current;
      setHasChanges(hasDataChanged);
      return hasDataChanged;
    } catch (error) {
      console.error('Error checking for changes:', error);
      return false;
    }
  }, [getCurrentData]);

  /**
   * Mark changes as saved (updates the initial data reference)
   */
  const markAsSaved = useCallback(() => {
    const currentData = getCurrentData();
    initialDataRef.current = JSON.stringify(currentData);
    setHasChanges(false);
  }, [getCurrentData]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setHasChanges(false);
  }, []);

  /**
   * Force set has changes state (useful for manual tracking)
   */
  const setHasChangesManually = useCallback((value: boolean) => {
    setHasChanges(value);
  }, []);

  return {
    hasChanges,
    checkForChanges,
    markAsSaved,
    reset,
    setHasChangesManually,
  };
}

/**
 * Simple version that just tracks a boolean flag
 * Useful when you don't want to compare data structures
 */
export function useUnsavedChangesSimple() {
  const [hasChanges, setHasChanges] = useState(false);

  const markAsChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  const markAsSaved = useCallback(() => {
    setHasChanges(false);
  }, []);

  const reset = useCallback(() => {
    setHasChanges(false);
  }, []);

  return {
    hasChanges,
    markAsChanged,
    markAsSaved,
    reset,
    setHasChanges,
  };
}

